'use client';

import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useToast } from '@/components/ui/use-toast';
import { FILE_VALIDATION, isFileTypeAllowed, validateFileSize } from '@/constants/variables';
import { useGalleryUploadSystem } from '@/hooks/mutations';
import { getVideoDuration } from '@/hooks/utils/useFileUpload';

/**
 * Validates a file's size and, if it's a video, its duration
 */
async function validateFile(file: File): Promise<{ valid: boolean; error?: string }> {
  // Check file size
  const sizeValidation = validateFileSize(file, FILE_VALIDATION.mediaGallery.maxSizeMB);
  if (!sizeValidation.valid) {
    return sizeValidation;
  }

  // Check if file type is allowed
  if (!isFileTypeAllowed(file)) {
    return {
      valid: false,
      error: `File type is not allowed. Allowed types: image/*, video/*`,
    };
  }

  // Check video duration if it's a video file
  if (file.type.startsWith('video/')) {
    try {
      const duration = await getVideoDuration(file);
      if (duration > FILE_VALIDATION.mediaGallery.maxVideoDurationSeconds) {
        return {
          valid: false,
          error: `Video duration (${duration.toFixed(1)} seconds) exceeds the maximum allowed (${FILE_VALIDATION.mediaGallery.maxVideoDurationSeconds} seconds)`,
        };
      }
    } catch (error) {
      console.error('Error checking video duration:', error);
      // If we can't determine duration, we'll still allow the upload
    }
  }

  return { valid: true };
}

type UseDragAndDropOptions = {
  onUploadComplete?: () => Promise<void>;
  disabled?: boolean;
  pollingStrategy?: 'list' | 'single';
  enableScrollLock?: boolean;
};

export function useDragAndDrop(options: UseDragAndDropOptions = {}) {
  const { onUploadComplete, disabled = false, pollingStrategy = 'list', enableScrollLock = false } = options;
  const [isDragActive, setIsDragActive] = useState(false);
  const dragCounterRef = useRef(0);
  const { toast } = useToast();
  const translate = useTranslations();

  // Use the centralized upload system
  const { uploadFiles } = useGalleryUploadSystem({
    pollingStrategy,
    multiple: true,
    enableScrollLock,
    onUploadComplete,
  });

  const handleDragEnter = useCallback((e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (disabled)
      return;

    dragCounterRef.current++;
    if (e.dataTransfer?.items && e.dataTransfer.items.length > 0) {
      setIsDragActive(true);
    }
  }, [disabled]);

  const handleDragLeave = useCallback((e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (disabled)
      return;

    dragCounterRef.current--;
    if (dragCounterRef.current === 0) {
      setIsDragActive(false);
    }
  }, [disabled]);

  const handleDragOver = useCallback((e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (disabled)
      return;

    if (e.dataTransfer) {
      e.dataTransfer.dropEffect = 'copy';
    }
  }, [disabled]);

  const handleDrop = useCallback(async (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (disabled)
      return;

    setIsDragActive(false);
    dragCounterRef.current = 0;

    const files = Array.from(e.dataTransfer?.files || []);
    if (files.length === 0)
      return;

    // Validate files before uploading
    const validFiles: File[] = [];
    const errors: string[] = [];

    for (const file of files) {
      const validation = await validateFile(file);
      if (validation.valid) {
        validFiles.push(file);
      } else {
        errors.push(`${file.name}: ${validation.error}`);
      }
    }

    // Show validation errors
    if (errors.length > 0) {
      toast({
        title: translate('upload.dropzone.fileValidationFailed'),
        description: errors.join('\n'),
        variant: 'error',
      });
    }

    // Upload valid files
    if (validFiles.length > 0) {
      uploadFiles(validFiles);
    }
  }, [disabled, uploadFiles, toast, translate]);

  const addEventListeners = useCallback((element: HTMLElement) => {
    element.addEventListener('dragenter', handleDragEnter);
    element.addEventListener('dragleave', handleDragLeave);
    element.addEventListener('dragover', handleDragOver);
    element.addEventListener('drop', handleDrop);

    return () => {
      element.removeEventListener('dragenter', handleDragEnter);
      element.removeEventListener('dragleave', handleDragLeave);
      element.removeEventListener('dragover', handleDragOver);
      element.removeEventListener('drop', handleDrop);
    };
  }, [handleDragEnter, handleDragLeave, handleDragOver, handleDrop]);

  // Reset drag state on escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isDragActive) {
        dragCounterRef.current = 0;
        setIsDragActive(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isDragActive]);

  return {
    isDragActive,
    addEventListeners,
  };
}
